'use client'

import _ from 'lodash'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useCallback, useState, useMemo } from 'react'
import slugify from 'slugify'
import NextLink from 'next/link'
import { HeartIcon } from '@heroicons/react/20/solid'
import { HeartIcon as HeartOutlineIcon } from '@heroicons/react/24/outline'
import {
  ArrowUpIcon,
  ArrowUpRightIcon,
  ArrowRightIcon,
  ArrowDownIcon,
  ChevronDoubleDownIcon,
} from '@heroicons/react/16/solid'
import { PriceDropIcon } from '../icon'

import { Vehicle, DataOne, lookup } from '@cruz-com/lookup'
import { Avatar, Bullet, CardPhoto, CardBody, CardItem, Divider, Separator, RatingStars } from '@/components'
import { pages } from '@/settings'
import { myCruzApi } from '@/api'
import { useGlobalStore } from '@/store/provider'

type Props = {
  v: Vehicle.Summary,
  showUser: boolean,
  showStockNumber?: boolean,
  showPhotoCount: boolean,
  ignoreKeys?: string[]
  onUnSaved?: () => void
}

dayjs.extend(relativeTime)

export function VehicleCard({ v, showUser, showStockNumber, showPhotoCount, ignoreKeys, onUnSaved }: Props) {
  const { user, setUser, setModalSignIn } = useGlobalStore((state) => state)
  const [isSaving, setIsSaving] = useState(false)

  const title = useCallback(() => {
    return [v?.year, v?.make, v?.model, v?.trim || ''].join(' ').trim() || '-'
  }, [v])

  const location = useCallback(() => {
    const { city, state_abbr, zip_code } = v?.location || {}
    return [city, state_abbr, zip_code].join(', ') || ''
  }, [v?.location])

  const href = useCallback(() => {
    return pages.vehicleDetail.href
      .replace('[location]', slugify(location()))
      .replace('[title]', slugify(title())) + '/' + v?._id
  }, [v?._id])

  const UserInfo = useMemo(() => {
    const userName = v?.user_display?.first_name + ' ' + v?.user_display?.last_name
    const userHref = pages.userVehicles.href.replace('[name]', slugify(userName)).replace('[id]', v.user_id)
    return (
      <div className='w-full px-4 py-2.5 border-t hover:bg-zinc-100 dark:hover:bg-zinc-800/50'>
        <NextLink hrefLang='en' className='font-semibold flex items-center gap-x-4' href={userHref}>
          <Avatar photo_url={v.user_display?.photo_url} className='size-10' name={userName} />
          <div>
            <div className='font-semibold text-base/6 md:text-sm truncate'>
              {userName}
            </div>
            <div className='font-light text-sm/6 truncate'>
              {v.user_display?.location?.city}, {v.user_display?.location?.state_abbr}
            </div>
          </div>
        </NextLink>
      </div>
    )
  }, [v?.user_display])

  // dealer info
  const DealerInfo = useMemo(() => {
    const { _id, name, city, state_abbr, zip_code, distance, place } = v?.dealer_location || {}
    // const locName = place?.display_name || name || ''
    const locName = name || place?.display_name || ''
    const dealerHref = pages.dealerVehicles.href
      .replace('[dealer]', slugify(locName || ''))
      .replace('[location]', slugify(`${city}, ${state_abbr}, ${zip_code}`))
      .replace('[id]', _id)
    return (
      <div className='w-full px-4 py-2.5 border-t overflow-hidden truncate hover:bg-zinc-100 dark:hover:bg-zinc-800/50'>
        <NextLink hrefLang='en' href={dealerHref} prefetch={true}>
          <div className='font-semibold text-base/6 md:text-sm truncate'>
            {locName}
          </div>
          <div className='font-light text-sm/6 truncate'>
            {city}, {state_abbr} {zip_code}
            {distance && <span className='ml-1'>({distance.toLocaleString()} mi. away)</span>}<br />
          </div>
        </NextLink>
      </div>
    )
  }, [v?.dealer_location])

  // mpg
  const MPG = useCallback(() => {
    let mpg: DataOne.MPG
    if (_.size(v.mpg_list) === 1) {
      mpg = _.first(v.mpg_list)
    } else {
      mpg = _.maxBy(v.mpg_list, (mpg) => { return mpg.city + mpg.highway })
    }
    if (_.isEmpty(mpg)) {
      return '-'
    }
    return (
      <div className='flex items-center gap-x-2'>
        <div>
          {mpg.city}<span className='text-xs font-light ml-1'>City</span>
        </div>
        <div className='border-r border-gray-400 h-4' />
        <div>
          {mpg.highway}<span className='text-xs font-light ml-1'>Highway</span>
        </div>
        {
          (_.size(v.mpg_list) > 1) &&
          <div className='ml-2 text-xs font-light'>
            ({(mpg.fuel_grade ? mpg.fuel_grade + ' ' : '') + mpg.fuel_type || ''}) ...
          </div>
        }
      </div>
    )
  }, [v.mpg_list])

  const EstimatedValue = () => {
    const { deal } = v?.vehicle_value || {}
    const { id, name } = _.find(lookup.vehicle.deal, { id: _.toNumber(deal) }) || {}
    if (!name) {
      return '-'
    }
    return (
      <div className={`deal-s deal-s-${id}`}>
        {id === Vehicle.DealEnum.Bad && <ArrowDownIcon className='size-4' />}
        {id === Vehicle.DealEnum.Fair && <ArrowRightIcon className='size-4' />}
        {id === Vehicle.DealEnum.Good && <ArrowUpRightIcon className='size-4' />}
        {id === Vehicle.DealEnum.Great && <ArrowUpIcon className='size-4' />}
        {id === Vehicle.DealEnum.Low && <ChevronDoubleDownIcon className='size-4' />}
        {name}
      </div >
    )
  }

  // price
  const Price = useCallback(() => {
    const price = v?.price || 0
    if (price > 0) {
      return (
        <div className='pl-4 flex items-center dark:font-semibold antialiased'>
          {
            v.sale_status_id === -1 &&
            <span className='text-base mr-2 font-semibold font-serif italic'>Sold:</span>
          }
          <sup className='text-base pt-3 font-light mr-0.5 subpixel-antialiased font-serif'>$</sup>
          <span className='font-serif font-semibold text-2xl tracking-wide'>{price.toLocaleString()}</span>
          {
            v.show_price_dropped && ((v?.history?.price_change?.value || 0) < 0) &&
            <PriceDropIcon className='size-7 ml-1 text-red-700' />
          }
        </div>
      )
    }
    if (v?.user_id) {
      return (
        <div className='px-4 font-semibold font-serif text-base truncate italic'>
          {v?.sale_status?.name}
        </div>
      )
    }
    return <div className='px-4 font-semibold text-base truncate italic font-serif'>Price Unknown</div>
  }, [v?.price, v?.sale_status_id])

  // mileage
  const Mileage = useCallback(() => {
    if (!v?.mileage || v.mileage === 0) {
      return <></>
    }
    return (
      <>
        <Separator />
        <div className='text-sm'>
          <span className='font-semibold'>{v?.mileage?.toLocaleString()}</span><span className='font-light ml-1'>miles</span>
        </div>
      </>
    )
  }, [v?.mileage])

  async function handleSaveVehicle() {
    if (_.isEmpty(user)) {
      setModalSignIn({ show: true, saveVehicleId: v._id })
      return
    }
    setIsSaving(true)
    const { data } = await myCruzApi.saveVehicle.toggleSave(v._id)
    setUser(_.assign(user, data), null)
    onUnSaved && onUnSaved()
    setIsSaving(false)
  }

  // saved
  const SaveVehicle = useCallback(() => {
    if (!_.isEmpty(user) && user?._id === v?.user_id) {
      return <></>
    }
    return (
      <button type='button' className='btn-save-vehicle' onClick={handleSaveVehicle} aria-label='Save Vehicle'>
        {
          isSaving ?
            <svg className="animate-spin size-5 text-red-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            :
            _.includes(user?.saved_vehicle_ids, v._id)
              ? <HeartIcon className='size-5 text-red-700' title='Remove' />
              : <HeartOutlineIcon className='size-5' title='Save' />
        }
      </button>
    )
  }, [user?._id, user?.saved_vehicle_ids, v?.user_id, isSaving])

  return (
    <div className='relative'>
      <SaveVehicle />
      <NextLink hrefLang='en' href={href()} prefetch={true}>
        <CardPhoto
          photoUrl={v?.photo_preview_url}
          photoCount={showPhotoCount ? v?.photo_count : null}
          isDeleted={v?.post_status_id === -2}
          isSold={v?.sale_status_id === -1}
        />
        <div className='px-4 pt-3 flex justify-between gap-4'>
          <div className='flex-1 text-lg md:text-base/8 font-semibold truncate'>
            {title()}
          </div>
        </div>
        <div className='flex items-center justify-between my-1'>
          <Price />
          <div className='w-fit mt-3 mb-2 px-4 py-2 bg-zinc-100 dark:bg-zinc-800 shadow-inner rounded-l-full flex items-center gap-x-2 truncate'>
            <div className='text-sm font-normal truncate'>{v.condition_name}</div>
            <Mileage />
          </div>
        </div >
        <CardBody>
          {
            showStockNumber &&
            <CardItem label='Stock Number' value={v?.reference?.stock_number || '-'} />
          }
          <CardItem label='Estimated Value' value={<EstimatedValue />} />
          <CardItem label='VIN' value={v?.vin || '-'} />
          {
            !_.includes(ignoreKeys, 'drive_type') &&
            <CardItem label='Drive Type' value={v?.drive_type_name || '-'} />
          }
          <CardItem label='Engine' value={v?.engine?.name || '-'} />
          {
            !_.includes(ignoreKeys, 'fuel_type') &&
            <CardItem label='Fuel Type' value={v?.engine?.fuel_type_name || '-'} />
          }
          <CardItem label='Transmission' value={v?.transmission?.name || '-'} />
          {
            !_.includes(ignoreKeys, 'mpg') &&
            <CardItem label='MPG' value={<MPG />} />
          }
          <CardItem label='Max Seating' value={v?.specification?.max_seating || '-'} />
          {
            !_.includes(ignoreKeys, 'safety_ratings') &&
            <CardItem label='Safety Ratings' value={(v?.safety_ratings || 0) > 0 ? <RatingStars rating={v?.safety_ratings || 0} size={4} /> : '-'} />
          }
        </CardBody>
      </NextLink>
      {
        (showUser && !_.isEmpty(v.user_display)) && UserInfo
      }
      {
        (showUser && !_.isEmpty(v.dealer_location)) && DealerInfo
      }
      <Divider />
      <div className='flex items-center justify-between font-light px-4 py-2'>
        <div className='flex items-center justify-between gap-x-2 text-xs/3'>
          <div>
            <span className='font-medium text-sm'>{v?.view_count?.toLocaleString()}</span> view{(v?.view_count || 0) > 1 && 's'}
          </div>
          <Bullet />
          <div>
            <span className='font-medium text-sm'>{v?.vote?.up?.count?.toLocaleString() || 0}</span> like{(v?.vote?.up?.count || 0) > 1 && 's'}
          </div>
        </div>
        <div className='text-xs'>
          Posted {dayjs(v.date_create).fromNow()}
        </div>
      </div>
    </div >
  )
}